import clsx from "clsx";
import React from "react";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

import routes from "common/routes";
import RoundedRectangleSegments from "components/Icons/RoundedRectangleSegments";
import Link from "components/Link";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { SortOrder } from "interfaces/SortOrder";
import { transformViewDate } from "utils/date-utils";

import useCommonStyles from "../styles";
import useStyles from "./styles";

type RecentRaceBlockProps = {
  className?: string;
  component?: React.FunctionComponent;
  to?: string;
};

const RecentRaceBlock = () => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const { currentUser } = useLoggedInUser();

  const selectedFacilityIds = useAppSelector(
    state => state?.queriesConfig?.selectedFacilityIds
  );

  const { useListRaceHistories } = useRacehorse360Api();

  const { data, isLoading } = useListRaceHistories(
    {
      query: {
        trainerIds: currentUser.trainerIds,
        isRaceOfficial: { value: true },
        horseFacilityIds: selectedFacilityIds
      },
      getOptions: {
        select: [
          "id",
          "horse",
          "earnings",
          "date",
          "facility.id",
          "facility.code",
          "facility.name",
          "raceNumber",
          "positionAtFinish"
        ],
        orderBy: [`date ${SortOrder.DESC}`]
      },
      pagingOptions: {
        maxResults: 1
      }
    },
    {
      onError: error => console.error(error)
    }
  );
  const recentRace = data?.raceHistories[0];

  const recentRaceBlockProps: RecentRaceBlockProps = {};
  if (recentRace?.horse) {
    recentRaceBlockProps.component = Link;
    recentRaceBlockProps.to = routes.horseDetails.path.replace(
      ":horseId",
      recentRace?.horse?.id
    );
    recentRaceBlockProps.className = commonClasses.pointerAvailable;
  }

  const renderTitle = () => {
    return (
      <Grid component={Typography} item className={classes.rrTitle} xs={12}>
        {recentRace?.horse?.name}
      </Grid>
    );
  };

  const renderRaceDetails = () => {
    return (
      <Grid component={"div"} item className={classes.ellipsisWrapper}>
        <Typography className={clsx(classes.rrTableHeader, classes.ellipsis)}>
          Recent Race
        </Typography>
        <Typography className={clsx(classes.rrTableCell, classes.ellipsis)}>
          {recentRace?.date
            ? `${transformViewDate(recentRace.date, null, "dMMMyy")}-${
                recentRace?.raceNumber
              }${recentRace?.facility?.code}`
            : ""}
        </Typography>
      </Grid>
    );
  };

  const renderRaceStatus = () => {
    if (!recentRace?.positionAtFinish) return;

    return (
      <Grid component={"div"} item className={classes.ellipsisWrapper}>
        <Typography className={clsx(classes.rrTableHeader, classes.ellipsis)}>
          Finish
        </Typography>
        <Typography className={classes.rrTableCell}>
          {recentRace?.positionAtFinish}
        </Typography>
      </Grid>
    );
  };

  return (
    <Grid container {...recentRaceBlockProps}>
      <Paper className={commonClasses.infoPaper}>
        <Grid item className={commonClasses.bgBadge}>
          <RoundedRectangleSegments className={commonClasses.roundedSegments} />
        </Grid>
        <Grid item xs={12}>
          <Grid container className={commonClasses.blockContent}>
            {isLoading && <Loader overlay />}
            {renderTitle()}
            <Grid item className={commonClasses.label} xs={12}>
              <Grid container className={classes.rrTable}>
                {renderRaceDetails()}
                {renderRaceStatus()}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default React.memo(RecentRaceBlock);
